<script setup lang="ts">
// "Pick a name" search list — DESIGN.md section 9.1, New match screen. Sorted by recency
// (backend default order); the search box is the one text input this kiosk has, so the scanner
// FSM stands down while it's focused (src/scanner.ts). It's also the only place the on-screen
// keyboard (src/components/OnScreenKeyboard.vue) ever mounts: strictly while this input has real
// DOM focus, never globally. Focus moving anywhere else (Close, a result row, or the keyboard's
// own Done key) blurs the input and the keyboard goes away with it.
import { computed, onMounted, ref, watch } from 'vue'
import { createGuest, getPlayers } from '../backend'
import type { Player } from '../types'
import Avatar from './Avatar.vue'
import OnScreenKeyboard from './OnScreenKeyboard.vue'

const emit = defineEmits<{ (e: 'pick', player: Player): void; (e: 'close'): void }>()

const query = ref('')
const results = ref<Player[]>([])
const loading = ref(false)
const creatingGuest = ref(false)
const inputEl = ref<HTMLInputElement | null>(null)
const keyboardOpen = ref(false)

function onKeyboardDone() {
  inputEl.value?.blur()
}

// "Add … as guest" appears once the typed name can't be an accidental tap and
// doesn't exactly match an existing player (case-insensitive).
const guestName = computed(() => query.value.trim())
const canAddGuest = computed(
  () =>
    guestName.value.length >= 2 &&
    !results.value.some((p) => p.displayName.toLowerCase() === guestName.value.toLowerCase()),
)

async function addGuest() {
  if (!canAddGuest.value || creatingGuest.value) return
  creatingGuest.value = true
  try {
    emit('pick', await createGuest(guestName.value))
  } finally {
    creatingGuest.value = false
  }
}

let debounceTimer: ReturnType<typeof setTimeout> | null = null

async function load() {
  loading.value = true
  try {
    const res = await getPlayers(query.value, 50)
    results.value = res.items
  } finally {
    loading.value = false
  }
}

watch(query, () => {
  if (debounceTimer) clearTimeout(debounceTimer)
  debounceTimer = setTimeout(load, 150)
})

onMounted(load)
</script>

<template>
  <div
    class="fixed inset-0 z-40 bg-black/80 flex items-center justify-center p-6"
    :style="keyboardOpen ? { paddingBottom: '246px' } : undefined"
    @click.self="emit('close')"
  >
    <div
      class="w-full max-w-2xl rounded-2xl bg-neutral-900 border border-neutral-700 flex flex-col overflow-hidden"
      :class="keyboardOpen ? 'h-[min(240px,35vh)]' : 'h-[min(440px,70vh)]'"
    >
      <div class="p-4 border-b border-neutral-800 flex items-center gap-3">
        <input
          ref="inputEl"
          v-model="query"
          type="text"
          placeholder="Search a name…"
          autofocus
          class="flex-1 tap-target rounded-xl bg-neutral-800 px-4 text-lg outline-none focus:ring-2 focus:ring-emerald-500"
          @focus="keyboardOpen = true"
          @blur="keyboardOpen = false"
        />
        <button type="button" class="tap-target px-4 rounded-xl bg-neutral-700 font-semibold" @click="emit('close')">Close</button>
      </div>
      <div class="flex-1 overflow-y-auto divide-y divide-neutral-800">
        <div v-if="loading && results.length === 0" class="p-6 text-neutral-500">Loading…</div>
        <div v-else-if="results.length === 0 && !canAddGuest" class="p-6 text-neutral-500">No players found</div>
        <button
          v-for="p in results"
          :key="p.id"
          type="button"
          class="w-full flex items-center gap-4 p-3 text-left tap-target active:bg-white/5"
          @click="emit('pick', p)"
        >
          <Avatar :player="p" :size="48" />
          <div class="flex-1 min-w-0">
            <div class="font-semibold truncate">
              {{ p.displayName }}
              <span v-if="p.isGuest" class="ml-1 text-xs font-normal text-neutral-500">guest</span>
            </div>
            <div class="text-sm text-neutral-500">Rapid {{ p.ratings.rapid.rating }}<span v-if="p.ratings.rapid.provisional">?</span></div>
          </div>
        </button>
        <button
          v-if="canAddGuest"
          type="button"
          class="w-full flex items-center gap-4 p-3 text-left tap-target active:bg-white/5 text-emerald-400"
          :disabled="creatingGuest"
          @click="addGuest"
        >
          <span class="w-12 h-12 rounded-full bg-neutral-800 flex items-center justify-center text-2xl">+</span>
          <span class="font-semibold">Add “{{ guestName }}” as guest</span>
        </button>
      </div>
    </div>

    <OnScreenKeyboard v-if="keyboardOpen" v-model="query" @done="onKeyboardDone" />
  </div>
</template>
